// src/pages/Settings/Settings.tsx
import React, { useState, useEffect } from 'react';
import './Settings.css';

// SVG Icons
const BellIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

const MoonIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
);

const GlobeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

const DownloadIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const TrashIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const ShieldIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const WarningIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 9v4M12 17h.01" />
    <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
  </svg>
);

interface SettingsData {
  notifications: {
    email: boolean;
    push: boolean;
    predictions: boolean;
  };
  appearance: {
    darkMode: boolean;
    compactView: boolean;
    showPredictions: boolean;
  };
  preferences: {
    defaultLeague: string;
    language: string;
    timezone: string;
  };
  privacy: {
    showEmail: boolean;
    showName: boolean;
  };
}

interface UserStats {
  totalPredictions: number;
  correctPredictions: number;
  accuracy: number;
}

const Settings: React.FC = () => {
  const [settings, setSettings] = useState<SettingsData>({
    notifications: {
      email: true,
      push: true,
      predictions: true
    },
    appearance: {
      darkMode: false,
      compactView: false,
      showPredictions: true
    },
    preferences: {
      defaultLeague: 'all',
      language: 'en',
      timezone: 'UTC'
    },
    privacy: {
      showEmail: false,
      showName: true
    }
  });
  
  const [stats, setStats] = useState<UserStats>({
    totalPredictions: 0,
    correctPredictions: 0,
    accuracy: 0
  });
  
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
  const [activeSection, setActiveSection] = useState('notifications');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [modalAction, setModalAction] = useState<'clear' | 'delete' | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

  // Load settings and stats on mount
  useEffect(() => {
    loadSettings();
    loadUserStats();
    
    // Apply dark mode from saved settings
    const savedSettings = localStorage.getItem('userSettings');
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        if (parsed.appearance?.darkMode) {
          document.body.classList.add('dark-mode');
        }
      } catch (e) {
        console.error('Failed to load settings');
      }
    }
  }, []);

  const loadSettings = () => {
    const savedSettings = localStorage.getItem('userSettings');
    if (savedSettings) {
      try {
        setSettings(JSON.parse(savedSettings));
      } catch (e) {
        console.error('Failed to load settings');
        showToast('Failed to load settings', 'error');
      }
    }
  };

  const loadUserStats = async () => {
    try {
      // Try to get stats from API
      const token = localStorage.getItem('authToken');
      if (token) {
        const response = await fetch('/api/users/stats/', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setStats({
            totalPredictions: data.total_predictions || 0,
            correctPredictions: data.correct_predictions || 0,
            accuracy: data.accuracy || 0
          });
          return;
        }
      }
      
      // Fallback to localStorage
      const savedPredictions = localStorage.getItem('predictions');
      if (savedPredictions) {
        const predictions = JSON.parse(savedPredictions);
        const total = predictions.length;
        const correct = predictions.filter((p: any) => p.isCorrect).length;
        setStats({
          totalPredictions: total,
          correctPredictions: correct,
          accuracy: total > 0 ? Math.round((correct / total) * 100) : 0
        });
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const showToast = (message: string, type: 'success' | 'error' | 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleSave = async () => {
    setSaveStatus('saving');
    
    try {
      // Save to localStorage
      localStorage.setItem('userSettings', JSON.stringify(settings));
      
      // Apply dark mode
      if (settings.appearance.darkMode) {
        document.body.classList.add('dark-mode');
      } else {
        document.body.classList.remove('dark-mode');
      }
      
      // Save to backend if authenticated
      const token = localStorage.getItem('authToken');
      if (token) {
        const response = await fetch('/api/users/settings/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(settings)
        });
        
        if (!response.ok) {
          throw new Error('Failed to save settings to server');
        }
      }
      
      setSaveStatus('success');
      showToast('Settings saved successfully!', 'success');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Failed to save settings:', error);
      setSaveStatus('error');
      showToast('Failed to save settings', 'error');
      setTimeout(() => setSaveStatus('idle'), 2000);
    }
  };

  const handleReset = () => {
    const defaultSettings: SettingsData = {
      notifications: {
        email: true,
        push: true,
        predictions: true
      },
      appearance: {
        darkMode: false,
        compactView: false,
        showPredictions: true
      },
      preferences: {
        defaultLeague: 'all',
        language: 'en',
        timezone: 'UTC'
      },
      privacy: {
        showEmail: false,
        showName: true
      }
    };
    
    setSettings(defaultSettings);
    localStorage.setItem('userSettings', JSON.stringify(defaultSettings));
    
    // Remove dark mode if it was applied
    document.body.classList.remove('dark-mode');
    
    showToast('Settings reset to default', 'success');
  };

  const handleExportData = async () => {
    try {
      let exportData: any = {
        settings,
        stats,
        exportDate: new Date().toISOString()
      };
      
      // Try to get predictions from API
      const token = localStorage.getItem('authToken');
      if (token) {
        const response = await fetch('/api/predictions/history/', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          exportData.predictions = await response.json();
        }
      }
      
      // Add local predictions
      const localPredictions = localStorage.getItem('predictions');
      if (localPredictions) {
        exportData.localPredictions = JSON.parse(localPredictions);
      }
      
      // Add user data
      const userStr = localStorage.getItem('user');
      if (userStr) {
        exportData.user = JSON.parse(userStr);
      }
      
      // Create and download JSON file
      const dataStr = JSON.stringify(exportData, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `zitech-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      showToast('Data exported successfully!', 'success');
    } catch (error) {
      console.error('Failed to export data:', error);
      showToast('Failed to export data', 'error');
    }
  };

  const handleExportCSV = () => {
    try {
      // Create CSV data
      const csvData = [
        ['Date', 'Match', 'Prediction', 'Actual', 'Result', 'Confidence'],
        ...(stats.totalPredictions > 0 ? [
          ['2024-01-01', 'Team A vs Team B', '2-1', '2-1', 'Correct', '75%'],
          ['2024-01-02', 'Team C vs Team D', '1-1', '2-1', 'Incorrect', '65%'],
        ] : [])
      ];
      
      const csvContent = csvData.map(row => row.join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `predictions-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      showToast('CSV exported successfully!', 'success');
    } catch (error) {
      showToast('Failed to export CSV', 'error');
    }
  };

  const handleClearHistory = () => {
    setModalAction('clear');
    setShowConfirmModal(true);
  };

  const confirmClearHistory = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (token) {
        const response = await fetch('/api/predictions/clear/', {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to clear history');
        }
      }
      
      // Clear local predictions
      localStorage.removeItem('predictions');
      
      // Reset stats
      setStats({
        totalPredictions: 0,
        correctPredictions: 0,
        accuracy: 0
      });
      
      showToast('Prediction history cleared', 'success');
      setShowConfirmModal(false);
    } catch (error) {
      console.error('Failed to clear history:', error);
      showToast('Failed to clear history', 'error');
    }
  };

  const handleDeleteAccount = () => {
    setModalAction('delete');
    setShowConfirmModal(true);
  };

  const confirmDeleteAccount = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (token) {
        const response = await fetch('/api/users/delete/', {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to delete account');
        }
      }
      
      // Clear all local data
      localStorage.clear();
      
      // Redirect to login
      window.location.href = '/login';
      
      showToast('Account deleted', 'success');
    } catch (error) {
      console.error('Failed to delete account:', error);
      showToast('Failed to delete account', 'error');
    }
  };

  const sections = [
    { id: 'notifications', label: 'Notifications', icon: <BellIcon /> },
    { id: 'appearance', label: 'Appearance', icon: <MoonIcon /> },
    { id: 'preferences', label: 'Preferences', icon: <GlobeIcon /> },
    { id: 'privacy', label: 'Privacy', icon: <ShieldIcon /> },
    { id: 'data', label: 'Data', icon: <DownloadIcon /> },
  ];

  return (
    <div className="settings-container">
      {/* Toast Notification */}
      {toast && (
        <div className={`toast toast-${toast.type}`}>
          {toast.type === 'success' && <CheckIcon />}
          {toast.type === 'error' && <CloseIcon />}
          <span>{toast.message}</span>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="modal-overlay" onClick={() => setShowConfirmModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <WarningIcon />
              <h3>{modalAction === 'clear' ? 'Clear History' : 'Delete Account'}</h3>
            </div>
            <div className="modal-body">
              {modalAction === 'clear' ? (
                <p>Are you sure you want to clear all your prediction history? This action cannot be undone.</p>
              ) : (
                <p>Are you sure you want to delete your account? All your data will be permanently removed. This action cannot be undone.</p>
              )}
            </div>
            <div className="modal-footer">
              <button className="modal-btn cancel" onClick={() => setShowConfirmModal(false)}>
                Cancel
              </button>
              <button 
                className="modal-btn confirm" 
                onClick={modalAction === 'clear' ? confirmClearHistory : confirmDeleteAccount}
              >
                {modalAction === 'clear' ? 'Clear History' : 'Delete Account'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="settings-wrapper">
        {/* Sidebar */}
        <div className="settings-sidebar">
          <div className="sidebar-header">
            <h2>Settings</h2>
            <p>Manage your preferences</p>
          </div>
          <nav className="settings-nav">
            {sections.map(section => (
              <button
                key={section.id}
                className={`nav-item ${activeSection === section.id ? 'active' : ''}`}
                onClick={() => setActiveSection(section.id)}
              >
                <span className="nav-icon">{section.icon}</span>
                <span className="nav-label">{section.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Main Content */}
        <div className="settings-main">
          <div className="settings-header">
            <h1>{sections.find(s => s.id === activeSection)?.label}</h1>
            <div className="header-actions">
              <button className="reset-btn" onClick={handleReset}>
                Reset to Default
              </button>
            </div>
          </div>

          <div className="settings-content">
            {/* Notifications Section */}
            {activeSection === 'notifications' && (
              <div className="settings-section">
                <div className="section-header">
                  <h3>Notification Preferences</h3>
                  <p>Choose how you want to be notified</p>
                </div>
                
                <div className="setting-group">
                  <div className="setting-item">
                    <div className="setting-info">
                      <span className="setting-label">Email Notifications</span>
                      <span className="setting-description">Receive prediction results via email</span>
                    </div>
                    <label className="toggle-switch">
                      <input 
                        type="checkbox" 
                        checked={settings.notifications.email}
                        onChange={(e) => setSettings({
                          ...settings,
                          notifications: { ...settings.notifications, email: e.target.checked }
                        })}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>

                  <div className="setting-item">
                    <div className="setting-info">
                      <span className="setting-label">Push Notifications</span>
                      <span className="setting-description">Real-time updates on your predictions</span>
                    </div>
                    <label className="toggle-switch">
                      <input 
                        type="checkbox" 
                        checked={settings.notifications.push}
                        onChange={(e) => setSettings({
                          ...settings,
                          notifications: { ...settings.notifications, push: e.target.checked }
                        })}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>

                  <div className="setting-item">
                    <div className="setting-info">
                      <span className="setting-label">Prediction Alerts</span>
                      <span className="setting-description">Get notified when new predictions are available</span>
                    </div>
                    <label className="toggle-switch">
                      <input 
                        type="checkbox" 
                        checked={settings.notifications.predictions}
                        onChange={(e) => setSettings({
                          ...settings,
                          notifications: { ...settings.notifications, predictions: e.target.checked }
                        })}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Appearance Section */}
            {activeSection === 'appearance' && (
              <div className="settings-section">
                <div className="section-header">
                  <h3>Appearance Settings</h3>
                  <p>Customize how the app looks</p>
                </div>
                
                <div className="setting-group">
                  <div className="setting-item">
                    <div className="setting-info">
                      <span className="setting-label">Dark Mode</span>
                      <span className="setting-description">Switch between light and dark theme</span>
                    </div>
                    <label className="toggle-switch">
                      <input 
                        type="checkbox" 
                        checked={settings.appearance.darkMode}
                        onChange={(e) => setSettings({
                          ...settings,
                          appearance: { ...settings.appearance, darkMode: e.target.checked }
                        })}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>

                  <div className="setting-item">
                    <div className="setting-info">
                      <span className="setting-label">Compact View</span>
                      <span className="setting-description">Show more content with compact spacing</span>
                    </div>
                    <label className="toggle-switch">
                      <input 
                        type="checkbox" 
                        checked={settings.appearance.compactView}
                        onChange={(e) => setSettings({
                          ...settings,
                          appearance: { ...settings.appearance, compactView: e.target.checked }
                        })}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>

                  <div className="setting-item">
                    <div className="setting-info">
                      <span className="setting-label">Show Predictions</span>
                      <span className="setting-description">Display prediction cards on dashboard</span>
                    </div>
                    <label className="toggle-switch">
                      <input 
                        type="checkbox" 
                        checked={settings.appearance.showPredictions}
                        onChange={(e) => setSettings({
                          ...settings,
                          appearance: { ...settings.appearance, showPredictions: e.target.checked }
                        })}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Preferences Section */}
            {activeSection === 'preferences' && (
              <div className="settings-section">
                <div className="section-header">
                  <h3>User Preferences</h3>
                  <p>Customize your experience</p>
                </div>
                
                <div className="setting-group">
                  <div className="setting-item">
                    <div className="setting-info">
                      <span className="setting-label">Default League</span>
                      <span className="setting-description">Your preferred league for predictions</span>
                    </div>
                    <select 
                      className="setting-select"
                      value={settings.preferences.defaultLeague}
                      onChange={(e) => setSettings({
                        ...settings,
                        preferences: { ...settings.preferences, defaultLeague: e.target.value }
                      })}
                    >
                      <option value="all">All Leagues</option>
                      <option value="PL">Premier League</option>
                      <option value="PD">La Liga</option>
                      <option value="BL1">Bundesliga</option>
                      <option value="SA">Serie A</option>
                      <option value="FL1">Ligue 1</option>
                    </select>
                  </div>

                  <div className="setting-item">
                    <div className="setting-info">
                      <span className="setting-label">Language</span>
                      <span className="setting-description">Choose your preferred language</span>
                    </div>
                    <select 
                      className="setting-select"
                      value={settings.preferences.language}
                      onChange={(e) => setSettings({
                        ...settings,
                        preferences: { ...settings.preferences, language: e.target.value }
                      })}
                    >
                      <option value="en">English</option>
                      <option value="es">Español</option>
                      <option value="fr">Français</option>
                      <option value="de">Deutsch</option>
                      <option value="it">Italiano</option>
                    </select>
                  </div>

                  <div className="setting-item">
                    <div className="setting-info">
                      <span className="setting-label">Timezone</span>
                      <span className="setting-description">Set your local timezone for match times</span>
                    </div>
                    <select 
                      className="setting-select"
                      value={settings.preferences.timezone}
                      onChange={(e) => setSettings({
                        ...settings,
                        preferences: { ...settings.preferences, timezone: e.target.value }
                      })}
                    >
                      <option value="UTC">UTC</option>
                      <option value="America/New_York">Eastern Time</option>
                      <option value="Europe/London">GMT</option>
                      <option value="Europe/Paris">Central European Time</option>
                      <option value="Asia/Tokyo">Japan Standard Time</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Privacy Section */}
            {activeSection === 'privacy' && (
              <div className="settings-section">
                <div className="section-header">
                  <h3>Privacy Controls</h3>
                  <p>Manage your privacy settings</p>
                </div>
                
                <div className="setting-group">
                  <div className="setting-item">
                    <div className="setting-info">
                      <span className="setting-label">Show Email</span>
                      <span className="setting-description">Display your email on your profile</span>
                    </div>
                    <label className="toggle-switch">
                      <input 
                        type="checkbox" 
                        checked={settings.privacy.showEmail}
                        onChange={(e) => setSettings({
                          ...settings,
                          privacy: { ...settings.privacy, showEmail: e.target.checked }
                        })}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>

                  <div className="setting-item">
                    <div className="setting-info">
                      <span className="setting-label">Show Name</span>
                      <span className="setting-description">Display your name on your profile</span>
                    </div>
                    <label className="toggle-switch">
                      <input 
                        type="checkbox" 
                        checked={settings.privacy.showName}
                        onChange={(e) => setSettings({
                          ...settings,
                          privacy: { ...settings.privacy, showName: e.target.checked }
                        })}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                </div>

                <div className="danger-zone">
                  <h4>Danger Zone</h4>
                  <div className="setting-item">
                    <div className="setting-info">
                      <span className="setting-label">Export All Data</span>
                      <span className="setting-description">Download your complete user data (JSON format)</span>
                    </div>
                    <button className="setting-btn secondary" onClick={handleExportData}>
                      <DownloadIcon />
                      Export JSON
                    </button>
                  </div>

                  <div className="setting-item">
                    <div className="setting-info">
                      <span className="setting-label warning">Delete Account</span>
                      <span className="setting-description warning">Permanently delete your account and all data</span>
                    </div>
                    <button className="setting-btn danger" onClick={handleDeleteAccount}>
                      Delete Account
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Data Section */}
            {activeSection === 'data' && (
              <div className="settings-section">
                <div className="section-header">
                  <h3>Data Management</h3>
                  <p>Manage your data and storage</p>
                </div>
                
                <div className="stats-cards">
                  <div className="stat-card">
                    <div className="stat-value">{stats.totalPredictions}</div>
                    <div className="stat-label">Predictions Made</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-value">{stats.correctPredictions}</div>
                    <div className="stat-label">Correct Predictions</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-value">{stats.accuracy}%</div>
                    <div className="stat-label">Success Rate</div>
                  </div>
                </div>

                <div className="setting-group">
                  <div className="setting-item">
                    <div className="setting-info">
                      <span className="setting-label">Export Prediction History</span>
                      <span className="setting-description">Download CSV of all your predictions</span>
                    </div>
                    <button className="setting-btn primary" onClick={handleExportCSV}>
                      <DownloadIcon />
                      Export CSV
                    </button>
                  </div>

                  <div className="setting-item">
                    <div className="setting-info">
                      <span className="setting-label">Clear Prediction History</span>
                      <span className="setting-description warning">Remove all prediction data (cannot be undone)</span>
                    </div>
                    <button className="setting-btn danger" onClick={handleClearHistory}>
                      <TrashIcon />
                      Clear History
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Save Button */}
            <div className="settings-footer">
              <button 
                className={`save-btn ${saveStatus}`}
                onClick={handleSave}
                disabled={saveStatus === 'saving'}
              >
                {saveStatus === 'saving' && <div className="spinner-small"></div>}
                {saveStatus === 'success' && <CheckIcon />}
                {saveStatus === 'idle' && 'Save Changes'}
                {saveStatus === 'saving' && 'Saving...'}
                {saveStatus === 'success' && 'Saved!'}
                {saveStatus === 'error' && 'Error - Try Again'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;